// @ts-nocheck
import { useEffect, useMemo, useRef, useState } from 'react';
import { Lock } from 'lucide-react';
import { ConfirmDialog } from '../../../../components/WorkflowFormPrimitives';
import {
  bulkUpsertShareholders,
  deleteShareholder,
  listShareholders,
} from '../../../../../../services/ShareholderApiClient';
import { updateAnalysisVoteRoomShutdown } from '../../../../../../services/AnalysisApiClient';
import {
  loadShareholdersOnce,
  mapApiRowToLocalShareholder,
  prepareEligibleForVoting,
  toApiExclusionType,
} from '../shared/voteRoomMapping';
import { INVALID_EMAIL_MESSAGE, isValidEmail } from '../../../../../../utils/email/isValidEmail';
import {
  DUPLICATE_SHAREHOLDER_MESSAGE,
  hasShareholderNameOrEmailConflict,
} from '../../../../../../utils/shareholder/shareholderMatchKey';
import { pushToast } from '../../../../../../utils/toast/toastStore';
import { requiredApproveSharesForThreshold } from '../shared/voteTallyMath';
import { formatImportSuccessMessage, formatSameCapTableFileMessage } from './ImportSummaryBanner';
import { parseCapTableCsv } from './parseCapTableCsv';
import { WizardStepper } from './WizardStepper';
import { ImportShareholdersStep } from './ImportShareholdersStep';
import { ReviewShareholdersStep } from './ReviewShareholdersStep';
import { ShutdownRuleStep } from './ShutdownRuleStep';
import { PreLaunchEmailCheckStep } from './PreLaunchEmailCheckStep';
import { LaunchVoteRoomStep } from './LaunchVoteRoomStep';
import { Section318ExclusionModal } from './Section318ExclusionModal';
import { useVoteRoomPrecheck } from './useVoteRoomPrecheck';
import { useWaiverLaunchReadiness } from './useWaiverLaunchReadiness';
import { isAllPrecheckVerified } from '../shared/voteRoomConstants';

const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MB
const INVALID_SHARES_MESSAGE = 'Shares must be greater than zero.';

const toLocalShareholder = (row) => ({
  ...mapApiRowToLocalShareholder(row),
  importedFromFileName: row.importedFromFileName || null,
});

/** Returns the first DQI whose name case-insensitively matches `name`, or `undefined`. */
const findDqiByName = (dqis, name) => dqis.find((d) => d.name.toLowerCase() === name.toLowerCase());

const toUpsertItem = (sh, fallbackImportedFromFileName = null) => ({
  id: sh.id && !String(sh.id).startsWith('sh-') ? sh.id : null,
  name: sh.name,
  email: sh.email || '',
  shares: sh.shares,
  excluded: !!sh.excluded,
  exclusionType: toApiExclusionType(sh.exclusionType),
  section318Relationship: sh.exclusionType === '318' ? sh.relationship || null : null,
  linkedDqiClientId: null,
  importedFromFileName: sh.importedFromFileName ?? fallbackImportedFromFileName ?? null,
});

/**
 * Vote Room setup wizard orchestrator. Owns shareholder list state + all persistence (initial load,
 * single-row upserts, bulk upsert on import, delete). The three step components are presentational.
 */
export const SetupPage = ({
  onLaunch,
  dqis,
  companyName,
  analysisId,
  locked = false,
  launching = false,
  launchError = null,
  shutdownSettings,
  onShutdownChange,
  onShutdownSaved,
}) => {
  const [shareholders, setShareholders] = useState([]);
  const [newRow, setNewRow] = useState({ name: '', email: '', shares: '' });
  const [manualEntryError, setManualEntryError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState(null);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [shutdownSaving, setShutdownSaving] = useState(false);
  const [step, setStep] = useState(locked ? 'launch' : 'import');
  const [show318Modal, setShow318Modal] = useState(null);
  const [removeShareholderConfirm, setRemoveShareholderConfirm] = useState(null);

  const refreshShareholders = async () => {
    if (!analysisId) return [];
    try {
      const rows = await listShareholders(analysisId);
      setShareholders(rows.map(toLocalShareholder));
      return rows;
    } catch (error) {
      pushToast(error instanceof Error ? error.message : 'Failed to reload shareholders.', 'error');
      return [];
    }
  };

  const {
    precheckSending,
    precheckPolling,
    precheckRefreshing,
    precheckPollTimedOut,
    precheckError,
    handleSendPrecheck,
    handleRefreshPrecheckStatus,
    resetPrecheckUi,
  } = useVoteRoomPrecheck({ analysisId, toLocalShareholder, setShareholders, refreshShareholders });

  const {
    readiness: waiverReadiness,
    waiverReady,
    loading: waiverReadinessLoading,
    error: waiverReadinessError,
    refresh: refreshWaiverReadiness,
  } = useWaiverLaunchReadiness(analysisId);

  const wasLockedAtMountRef = useRef(locked);
  useEffect(() => {
    if (locked && !wasLockedAtMountRef.current) {
      wasLockedAtMountRef.current = true;
      setStep('launch');
    }
  }, [locked]);

  const saveSeqRef = useRef(0);
  const removeSeqRef = useRef(0);

  const runUpsert = async (items, replaceMissing, { successToast } = {}) => {
    if (!analysisId) return [];
    const payloadItems = replaceMissing ? items : items.slice(-1);
    const seq = ++saveSeqRef.current;
    setSaveStatus('saving');
    try {
      const rows = await bulkUpsertShareholders(analysisId, payloadItems, { replaceMissing });
      if (seq === saveSeqRef.current) {
        setSaveStatus('idle');
        if (successToast) pushToast(successToast, 'success');
      }
      return rows;
    } catch (error) {
      if (seq === saveSeqRef.current) setSaveStatus('error');
      throw error;
    }
  };

  const updateOneAndPersist = async (id, updater) => {
    let updatedRow = null;
    setShareholders((prev) =>
      prev.map((s) => {
        if (s.id !== id) return s;
        updatedRow = updater(s);
        return updatedRow;
      })
    );
    if (!updatedRow || !analysisId) return;
    try {
      await runUpsert([toUpsertItem(updatedRow)], false);
    } catch (error) {
      pushToast(error instanceof Error ? error.message : 'Failed to save shareholder change.', 'error');
    }
  };

  const persistImported = async (parsed, fileName) => {
    setShareholders(parsed);
    setUploadedFileName(fileName);
    const dqiMatchCount = parsed.filter((sh) => sh.exclusionType === 'dqi').length;
    const successToast = formatImportSuccessMessage(parsed.length, fileName, dqiMatchCount);

    if (!analysisId) {
      pushToast(successToast, 'success');
      return;
    }
    try {
      const rows = await runUpsert(
        parsed.map((sh) => toUpsertItem(sh, fileName)),
        true,
        { successToast }
      );
      const saved = rows.length > 0 ? rows.map(toLocalShareholder) : parsed;
      if (rows.length > 0) setShareholders(saved);
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Failed to save imported shareholders.');
    }
  };

  const handleFileUpload = (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['csv', 'tsv', 'txt'].includes(ext)) {
      setUploadError('Please upload a CSV or TSV file. For Excel (.xlsx), export as CSV first.');
      return;
    }

    const normalizedName = file.name.trim().toLowerCase();
    const lastImportedName = uploadedFileName || shareholders.map((s) => s.importedFromFileName).find(Boolean) || null;
    if (
      lastImportedName &&
      normalizedName === String(lastImportedName).trim().toLowerCase() &&
      shareholders.length > 0
    ) {
      setUploadError(null);
      pushToast(formatSameCapTableFileMessage(file.name), 'info');
      return;
    }

    if (file.size > MAX_FILE_BYTES) {
      setUploadError(`File is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum allowed size is 5 MB.`);
      return;
    }

    setUploadError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = parseCapTableCsv(e.target.result, file.name);
      if (result.error) {
        setUploadError(result.error);
        return;
      }
      const matched = result.rows.map((sh) => {
        const match = findDqiByName(dqis, sh.name);
        return match ? { ...sh, excluded: true, exclusionType: 'dqi', attributedTo: match.name } : sh;
      });
      void persistImported(matched, file.name);
    };
    reader.onerror = () => {
      setUploadError('Could not read the file. Please try again.');
    };
    reader.readAsText(file);
  };

  useEffect(() => {
    let cancelled = false;
    if (!analysisId) {
      setShareholders([]);
      return undefined;
    }

    const load = async () => {
      try {
        const rows = await loadShareholdersOnce(analysisId);
        if (cancelled) return;
        setShareholders(rows.map(toLocalShareholder));
      } catch (error) {
        if (!cancelled) {
          setUploadError(error instanceof Error ? error.message : 'Failed to load saved shareholders.');
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [analysisId]);

  const updateNewRow = (updater) => {
    setNewRow(updater);
    setManualEntryError(null);
  };

  const addRow = async () => {
    if (!newRow.name.trim() || !newRow.shares) return;
    const shares = parseInt(newRow.shares, 10);
    if (isNaN(shares) || shares <= 0) {
      setManualEntryError(INVALID_SHARES_MESSAGE);
      return;
    }
    const email = newRow.email.trim();
    if (!isValidEmail(email)) {
      setManualEntryError(INVALID_EMAIL_MESSAGE);
      return;
    }
    const name = newRow.name.trim();
    if (hasShareholderNameOrEmailConflict(shareholders, name, email)) {
      setManualEntryError(DUPLICATE_SHAREHOLDER_MESSAGE);
      return;
    }
    const match = findDqiByName(dqis, name);
    const added = {
      id: `sh-${Date.now()}`,
      name,
      email,
      shares,
      excluded: !!match,
      exclusionType: match ? 'dqi' : null,
      attributedTo: match?.name || null,
      relationship: null,
      importedFromFileName: null,
    };
    setShareholders((p) => [...p, added]);
    setNewRow({ name: '', email: '', shares: '' });
    if (!analysisId) {
      pushToast('Shareholders saved.', 'success');
      return;
    }
    try {
      const rows = await runUpsert([toUpsertItem(added)], false, { successToast: 'Shareholders saved.' });
      if (rows.length === 1) {
        const saved = toLocalShareholder(rows[0]);
        setShareholders((prev) => prev.map((s) => (s.id === added.id ? saved : s)));
      }
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Failed to save shareholder.');
    }
  };

  const removeRow = async (id) => {
    const previous = shareholders;
    setShareholders((p) => p.filter((s) => s.id !== id));
    if (!analysisId || String(id).startsWith('sh-')) return;
    const seq = ++removeSeqRef.current;
    setSaveStatus('saving');
    try {
      await deleteShareholder(analysisId, id);
      if (seq === removeSeqRef.current) setSaveStatus('idle');
    } catch (error) {
      if (seq === removeSeqRef.current) setSaveStatus('error');
      setShareholders(previous);
      pushToast(error instanceof Error ? error.message : 'Failed to remove shareholder.', 'error');
    }
  };

  const reinclude = (id) =>
    updateOneAndPersist(id, (s) => ({
      ...s,
      excluded: false,
      exclusionType: null,
      attributedTo: null,
      relationship: null,
    }));

  const excludeAsDqi = (id) => {
    const sh = shareholders.find((s) => s.id === id);
    if (!sh) return;
    const match = findDqiByName(dqis, sh.name);
    void updateOneAndPersist(id, (s) => ({
      ...s,
      excluded: true,
      exclusionType: 'dqi',
      attributedTo: match?.name || sh.name,
    }));
  };

  const excludeAs318 = (id, dqiName, rel) => {
    void updateOneAndPersist(id, (s) => ({
      ...s,
      excluded: true,
      exclusionType: '318',
      attributedTo: dqiName,
      relationship: rel,
    }));
  };

  const {
    totalShares,
    eligibleShares,
    eligibleShareholders,
    excludedShareholders,
    dqiExcluded,
    s318Excluded,
    threshold75,
    hasEmails,
    allEligiblePrecheckVerified,
  } = useMemo(() => {
    const total = shareholders.reduce((s, sh) => s + sh.shares, 0);
    const eligible = shareholders.filter((s) => !s.excluded);
    const excluded = shareholders.filter((s) => s.excluded);
    const excShares = excluded.reduce((s, sh) => s + sh.shares, 0);
    const elig = total - excShares;
    return {
      totalShares: total,
      eligibleShares: elig,
      eligibleShareholders: eligible,
      excludedShareholders: excluded,
      dqiExcluded: excluded.filter((s) => s.exclusionType === 'dqi'),
      s318Excluded: excluded.filter((s) => s.exclusionType === '318'),
      threshold75: requiredApproveSharesForThreshold(elig),
      hasEmails: eligible.every((s) => isValidEmail(s.email)),
      allEligiblePrecheckVerified: isAllPrecheckVerified(eligible),
    };
  }, [shareholders]);

  const handleLaunchClick = () => {
    const prepared = prepareEligibleForVoting(eligibleShareholders, totalShares);
    onLaunch(prepared, eligibleShares, excludedShareholders);
  };

  const onRequestRemoveRow = (target) => setRemoveShareholderConfirm(target);
  const show318Shareholder = show318Modal ? shareholders.find((s) => s.id === show318Modal) || null : null;

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">Vote Room Setup</p>
          <p className="text-xs text-gray-500 mt-0.5">
            {companyName} — Configure shareholders for the 280G approval vote.
          </p>
        </div>
      </div>

      {locked && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 text-xs text-amber-800 flex items-start gap-2">
          <Lock size={13} className="mt-0.5 flex-shrink-0 text-amber-600" />
          <div>
            <span className="font-semibold">Vote Room Launched.</span>{' '}
            <span>The cap table is read-only. Shareholders cannot be added, edited, or removed.</span>
          </div>
        </div>
      )}

      <div className="pb-4">
        <WizardStepper step={step} />
      </div>

      {step === 'import' && (
        <ImportShareholdersStep
          locked={locked}
          dragOver={dragOver}
          setDragOver={setDragOver}
          onFile={handleFileUpload}
          uploadError={uploadError}
          analysisId={analysisId}
          saveStatus={saveStatus}
          shareholders={shareholders}
          newRow={newRow}
          setNewRow={updateNewRow}
          manualEntryError={manualEntryError}
          onAddRow={addRow}
          onRequestRemoveRow={onRequestRemoveRow}
          onContinue={() => setStep('review')}
        />
      )}

      {step === 'review' && (
        <ReviewShareholdersStep
          locked={locked}
          uploadedFileName={uploadedFileName}
          shareholders={shareholders}
          eligibleShareholders={eligibleShareholders}
          excludedShareholders={excludedShareholders}
          dqiExcludedCount={dqiExcluded.length}
          s318ExcludedCount={s318Excluded.length}
          threshold75={threshold75}
          totalShares={totalShares}
          eligibleShares={eligibleShares}
          hasEmails={hasEmails}
          dqis={dqis}
          onExcludeAsDqi={excludeAsDqi}
          onOpen318Modal={(id) => setShow318Modal(id)}
          onReinclude={reinclude}
          onRequestRemoveRow={onRequestRemoveRow}
          onBack={() => setStep('import')}
          onContinue={() => setStep('shutdown')}
        />
      )}

      {step === 'shutdown' && (
        <ShutdownRuleStep
          settings={shutdownSettings}
          onChange={onShutdownChange}
          locked={locked}
          saving={shutdownSaving}
          onBack={() => setStep('review')}
          onContinue={async (nextSettings) => {
            onShutdownChange(nextSettings);
            if (!analysisId) {
              setStep('precheck');
              return;
            }
            setShutdownSaving(true);
            try {
              const updated = await updateAnalysisVoteRoomShutdown(analysisId, {
                shutdownMode: nextSettings.mode,
                shutdownCustomThreshold: nextSettings.mode === 'threshold_custom' ? nextSettings.customThreshold : null,
                shutdownHours: nextSettings.mode === 'time_based' ? nextSettings.hours : null,
              });
              if (updated && onShutdownSaved) onShutdownSaved(updated);
              setStep('precheck');
            } catch (error) {
              pushToast(error instanceof Error ? error.message : 'Failed to save shutdown settings.', 'error');
            } finally {
              setShutdownSaving(false);
            }
          }}
        />
      )}

      {step === 'precheck' && (
        <PreLaunchEmailCheckStep
          eligibleShareholders={eligibleShareholders}
          allEligiblePrecheckVerified={allEligiblePrecheckVerified}
          precheckSending={precheckSending}
          precheckPolling={precheckPolling}
          precheckRefreshing={precheckRefreshing}
          precheckPollTimedOut={precheckPollTimedOut}
          precheckError={precheckError}
          onBack={() => {
            resetPrecheckUi();
            setStep('shutdown');
          }}
          onRefreshPrecheckStatus={handleRefreshPrecheckStatus}
          onSendPrecheck={handleSendPrecheck}
          onContinue={() => {
            void refreshWaiverReadiness();
            setStep('launch');
          }}
        />
      )}

      {step === 'launch' && (
        <LaunchVoteRoomStep
          shareholders={shareholders}
          eligibleShareholders={eligibleShareholders}
          excludedShareholders={excludedShareholders}
          threshold75={threshold75}
          eligibleShares={eligibleShares}
          hasEmails={hasEmails}
          precheckReady={allEligiblePrecheckVerified}
          waiverReady={waiverReady}
          waiverReadiness={waiverReadiness}
          waiverReadinessLoading={waiverReadinessLoading}
          waiverReadinessError={waiverReadinessError}
          onRefreshWaiverReadiness={refreshWaiverReadiness}
          shutdownSettings={shutdownSettings}
          locked={locked}
          launching={launching}
          launchError={launchError}
          onBack={() => setStep('precheck')}
          onLaunch={handleLaunchClick}
        />
      )}

      <Section318ExclusionModal
        shareholder={show318Shareholder}
        dqis={dqis}
        onCancel={() => setShow318Modal(null)}
        onConfirm={(id, dqiName, rel) => {
          excludeAs318(id, dqiName, rel);
          setShow318Modal(null);
        }}
      />

      <ConfirmDialog
        open={!!removeShareholderConfirm}
        title="Remove Shareholder"
        description={
          removeShareholderConfirm
            ? `Remove ${removeShareholderConfirm.name} from the cap table? This cannot be undone.`
            : undefined
        }
        confirmLabel="Remove"
        zClassName="z-[60]"
        onCancel={() => setRemoveShareholderConfirm(null)}
        onConfirm={() => {
          if (removeShareholderConfirm) removeRow(removeShareholderConfirm.id);
          setRemoveShareholderConfirm(null);
        }}
      />
    </div>
  );
};
